import { useState } from "react";
import type { ComponentType, CSSProperties } from "react";
import {
  Antenna,
  BrainCircuit,
  CheckCircle,
  Cog,
  Compass,
  Cpu,
  Crosshair,
  Eye,
  GraduationCap,
  Link2,
  Lock,
  Mail,
  MapPin,
  Maximize2,
  Menu,
  MessageCircle,
  MonitorCog,
  Plus,
  Radar,
  RadioTower,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  SunMedium,
  Target,
  Trophy,
  Users,
  Wrench,
  X,
} from "lucide-react";

import { CONTACT, NAV_IDS } from "../data/site";
import { useActiveSection, useScrolledPastHero, useScrollReveal } from "../hooks/useSiteChrome";
import { ContactSection } from "./ContactSection";
import { Logo } from "./Logo";
import { ServiceArt } from "./ServiceArt";
import type { ServiceArtKind } from "./ServiceArt";
import { SiteNav } from "./SiteNav";
import { WhatsAppFab } from "./WhatsAppFab";

// Web-sized WebP copies produced by scripts/optimize-images.mjs.
import focPhoto from "../assets/optimized/FOC.webp";
import fpvPhoto from "../assets/optimized/FPV_Kamikaze.webp";
import soccerPhoto from "../assets/optimized/Soccer Drone.webp";
import trainerPhoto from "../assets/optimized/Trainer Drone.webp";
import rfPhoto from "../assets/optimized/RF_Training.webp";
import uasPhoto from "../assets/optimized/Drone in Noisy Environ.webp";
import gcsPhoto from "../assets/optimized/GCS.webp";

type IconComponent = ComponentType<{ size?: number; strokeWidth?: number }>;

type ProductItem = {
  /** Product photograph. */
  photo?: string;
  /** Generated vector artwork, used where there is no photograph. */
  art?: ServiceArtKind;
  /** Cutouts with empty backgrounds must not be cropped. */
  fit?: "contain";
  icon: IconComponent;
  title: string;
  body: string;
  /** Turns the card into a link. */
  href?: string;
};

type ProductGroup = {
  id: string;
  label: string;
  icon: IconComponent;
  blurb: string;
  items: ProductItem[];
};

const heroPillars = [
  { icon: RadioTower, label: "EW-Resilient\nTechnologies" },
  { icon: SunMedium, label: "Indigenous\nInnovation" },
  { icon: Crosshair, label: "Mission\nFocused" },
];

const proofPoints = [
  {
    icon: RadioTower,
    title: "See Through\nthe Noise",
    body: "Advanced RF\ndetection & AI\nclassification",
  },
  {
    icon: Link2,
    title: "Communicate\nWithout\nCompromise",
    body: "Fiber optic &\nRF-resilient\ndata links",
  },
  {
    icon: Crosshair,
    title: "Strike With\nPrecision",
    body: "Interceptor drones\n& mission-ready\nsolutions",
  },
  {
    icon: ShieldCheck,
    title: "Stay\nProtected",
    body: "EW-resilient\nsystems built for\nreal-world threats",
  },
];

const aboutValues = [
  {
    icon: Target,
    title: "Built For The Real Battlefield",
    body: "Every system is designed against jamming, spoofing and spectrum denial — not just for clean lab conditions.",
  },
  {
    icon: Cog,
    title: "Designed & Made In India",
    body: "Indigenous engineering across hardware, firmware and software, so critical capability stays fully in your control.",
  },
  {
    icon: Users,
    title: "From Trainer To Frontline",
    body: "One engineering team behind our training platforms, commercial fleets and defence-grade deployments.",
  },
  {
    icon: ShieldCheck,
    title: "Supported For The Long Run",
    body: "Spares, servicing, upgrades and operator training keep your fleet flying long after handover.",
  },
];

/** Products grouped into the three lines we actually sell. */
const productGroups: ProductGroup[] = [
  {
    id: "uav",
    label: "UAVs & Drones",
    icon: Crosshair,
    blurb: "Airframes built to fly, fight and train in contested airspace.",
    items: [
      {
        photo: focPhoto,
        icon: Link2,
        title: "Fiber Optic Communication Drone",
        body: "Tethered drone system with fiber optic link for jam-proof, secure and high-bandwidth communication.",
      },
      {
        photo: fpvPhoto,
        icon: Crosshair,
        title: "FPV Kamikaze Drone",
        body: "First-person-view strike platform for precision engagement of high-value targets, built to stay flyable and accurate in jammed airspace.",
      },
      {
        photo: soccerPhoto,
        fit: "contain",
        icon: Trophy,
        title: "Soccer Drone",
        body: "Caged drone-soccer platform for tournaments and competitive leagues. Rugged, safe to fly indoors and quick to repair between matches.",
      },
      {
        photo: trainerPhoto,
        icon: GraduationCap,
        title: "Trainer Drone",
        body: "Build-it-yourself and ready-to-fly trainer platforms for academies, institutes and training centres — airframe, controller and instructor support included.",
      },
      {
        icon: Plus,
        title: "And Many More",
        body: "Purpose-built airframes for other mission profiles. Tell us the requirement and we will engineer the platform around it.",
        href: "#contact",
      },
    ],
  },
  {
    id: "software",
    label: "Software & Simulation",
    icon: Cpu,
    blurb: "Signal, sensor and mission software that turns raw data into decisions.",
    items: [
      {
        photo: rfPhoto,
        fit: "contain",
        icon: RadioTower,
        title: "RF Training Simulator",
        body: "Advanced simulation environment for RF operations, signal analysis and EW tactics training.",
      },
      {
        photo: uasPhoto,
        fit: "contain",
        icon: Radar,
        title: "UAS Detection in Noisy Environment",
        body: "AI-powered detection system that identifies and classifies drones in complex RF noise and clutter.",
      },
      {
        photo: gcsPhoto,
        fit: "contain",
        icon: MonitorCog,
        title: "Durgastra GCS — Universal Ground Control System",
        body: "One ground control station for the whole fleet: mission planning, live telemetry, payload control and post-flight review across different airframes.",
      },
    ],
  },
  {
    id: "services",
    label: "Other Services",
    icon: Wrench,
    blurb: "Engineering, upkeep and operator training around the airframe.",
    items: [
      {
        art: "training",
        icon: ShieldCheck,
        title: "Military & Defence Training",
        body: "Operator and technician training for uniformed and defence-affiliated teams: flying under jamming and spectrum denial, RF detection and counter-UAS drills, field servicing and mission planning.",
      },
      {
        art: "build",
        icon: Rocket,
        title: "Custom Drone Builds",
        body: "Purpose-built airframes and integrations for survey, inspection, tethered comms and specialised mission profiles — engineered to your requirement.",
      },
      {
        art: "maintenance",
        icon: Wrench,
        title: "Maintenance & Repair",
        body: "Diagnostics, crash recovery, structural repair, calibration, firmware updates and service contracts for fleets of any size.",
      },
      {
        art: "engineering",
        icon: Cog,
        title: "Product Engineering Service of Defence Systems",
        body: "End-to-end engineering support for defence programmes — concept, design, prototyping, integration and qualification of mission systems.",
      },
    ],
  },
];

const capabilities = [
  { icon: RadioTower, title: "EW-Resilient\nCommunication" },
  { icon: ShieldAlert, title: "Anti-Jam &\nSpoof Resistance" },
  { icon: Radar, title: "Spectrum Monitoring\n& SIGINT" },
  { icon: Crosshair, title: "Counter-UAS\n& Threat Defeat" },
  { icon: Lock, title: "Encrypted\nSecure Datalinks" },
  { icon: Compass, title: "GNSS-Denied\nNavigation" },
  { icon: BrainCircuit, title: "AI/ML-Powered\nAnalytics" },
  { icon: Cpu, title: "Onboard\nEdge Processing" },
  { icon: Eye, title: "Sensor Fusion\n& Target Tracking" },
  { icon: Antenna, title: "Low-Latency\nTelemetry & Video" },
  { icon: Cog, title: "Indigenous\nDesign & Development" },
  { icon: CheckCircle, title: "Real-World\nProven" },
  { icon: Maximize2, title: "Mission\nAdaptable" },
];

const delay = (index: number, step = 70) => ({ "--d": `${index * step}ms` }) as CSSProperties;

const careersMail = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Application — Durgastra Innovations")}`;

export const App = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const active = useActiveSection(NAV_IDS);
  const scrolled = useScrolledPastHero();

  useScrollReveal();

  const closeMenu = () => setMenuOpen(false);

  return (
    <main className="site">
      <WhatsAppFab />

      <div className={`floating-nav${scrolled ? " is-visible" : ""}`}>
        <a className="floating-brand" href="#home" aria-label="Durgastra Innovations home">
          <Logo />
        </a>
        <nav aria-label="Section navigation">
          <SiteNav active={active} />
        </nav>
        <a className="contact-button" href="#contact">
          Get in Touch
        </a>
      </div>

      <section className="hero-section" id="home">
        <header className="top-nav" aria-label="Primary navigation">
          <a className="brand" href="#home" aria-label="Durgastra Innovations home">
            <Logo />
          </a>
          <nav className={menuOpen ? "is-open" : undefined}>
            <SiteNav active={active} onNavigate={closeMenu} />
          </nav>
          <a className="contact-button" href="#contact" onClick={closeMenu}>
            Get in Touch
          </a>
          <button
            type="button"
            className="menu-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </header>

        <div className="hero-copy">
          <h1>
            Building Solutions
            <br />
            That Stay Ahead Of
            <br />
            <span>Electronic Warfare.</span>
          </h1>
          <p>
            Durgastra Innovations develops advanced technologies and mission-ready solutions that
            dominate the spectrum, protect operations, and ensure decision superiority.
          </p>

          <div className="hero-pillar-row" aria-label="Key qualities">
            {heroPillars.map(({ icon: Icon, label }, index) => (
              <div className="hero-pillar" key={label} style={delay(index, 110)}>
                <Icon size={30} strokeWidth={1.55} />
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div className="hero-actions">
            <a className="primary-action" href="#solutions">
              Our Solutions
            </a>
            <a className="secondary-action" href="#products">
              Explore Products
            </a>
          </div>
        </div>

        <div className="hero-art" aria-hidden="true" />
      </section>

      <section className="problem-band" id="solutions">
        <div className="problem-title" data-reveal>
          Not just another
          <br />
          drone company.
          <br />
          We solve the
          <br />
          problems others
          <br />
          can’t.
        </div>
        <div className="problem-divider" />
        <div className="problem-content">
          <p data-reveal>
            Electronic Warfare is the modern battlefield. We build systems that see through the
            noise, communicate without compromise, and strike with precision.
          </p>
          <div className="proof-grid">
            {proofPoints.map(({ icon: Icon, title, body }, index) => (
              <article className="proof-card" key={title} data-reveal style={delay(index)}>
                <span className="icon-ring">
                  <Icon size={26} strokeWidth={1.5} />
                </span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section" id="about">
        <div className="about-head" data-reveal>
          <span className="eyebrow">About Us</span>
          <h2>Engineering For The Contested Spectrum</h2>
          <div className="capability-rule" />
          <p>
            Durgastra Innovations is an Ahmedabad-based deep-tech company building drones, RF systems
            and AI software for environments where the spectrum is crowded, contested or actively
            denied. We design, build, train and support — end to end, in India.
          </p>
        </div>

        <div className="about-grid">
          {aboutValues.map(({ icon: Icon, title, body }, index) => (
            <article className="about-card" key={title} data-reveal style={delay(index)}>
              <span className="icon-ring">
                <Icon size={24} strokeWidth={1.55} />
              </span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="products-section" id="products">
        <div className="products-head" data-reveal>
          <span className="eyebrow">Products &amp; Services</span>
          <h2>What We Build</h2>
          <div className="capability-rule" />
          <p>End-to-end solutions engineered for EW-dominated environments.</p>
        </div>

        {productGroups.map(({ id, label, icon: GroupIcon, blurb, items }) => (
          <div className="product-group" key={id}>
            <div className="group-head" data-reveal>
              <span className="group-icon">
                <GroupIcon size={20} strokeWidth={1.7} />
              </span>
              <div>
                <h3>{label}</h3>
                <p>{blurb}</p>
              </div>
            </div>

            <div className="product-grid">
              {items.map(({ photo, art, fit, icon: Icon, title, body, href }, index) => {
                const hasArt = Boolean(photo || art);

                const inner = (
                  <>
                    {photo && (
                      <div className={`product-image${fit ? " is-contain" : ""}`}>
                        <img src={photo} alt={title} loading="lazy" decoding="async" />
                      </div>
                    )}
                    {art && (
                      <div className="product-image product-image-art" aria-hidden="true">
                        <ServiceArt kind={art} />
                      </div>
                    )}
                    <span className="product-icon">
                      <Icon size={24} strokeWidth={1.55} />
                    </span>
                    <h4>{title}</h4>
                    <p>{body}</p>
                  </>
                );

                const className = `product-card${hasArt ? "" : " product-card-plain"}${
                  href ? " product-card-link" : ""
                }`;

                return href ? (
                  <a className={className} key={title} href={href} data-reveal style={delay(index)}>
                    {inner}
                  </a>
                ) : (
                  <article className={className} key={title} data-reveal style={delay(index)}>
                    {inner}
                  </article>
                );
              })}
            </div>
          </div>
        ))}

        <a className="primary-action wide centered" href="#contact">
          Request A Quote Or Schedule Training
        </a>
      </section>

      <section className="capabilities-section" id="capabilities">
        <h2 data-reveal>Capabilities That Make The Difference</h2>
        <div className="capability-rule" />
        <div className="capability-grid">
          {capabilities.map(({ icon: Icon, title }, index) => (
            <div className="capability" key={title} data-reveal style={delay(index, 45)}>
              <Icon size={30} strokeWidth={1.5} />
              <span>{title}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="careers-section" id="careers">
        <div className="careers-head" data-reveal>
          <span className="eyebrow">Careers</span>
          <h2>Build What Doesn’t Exist Yet</h2>
          <div className="capability-rule" />
          <p>
            We are a small, hands-on team solving hard problems in RF, autonomy and airframe design.
            If you would rather build a system than a slide deck, we should talk.
          </p>
        </div>

        <p className="careers-note" data-reveal>
          We hire for capability, not job titles. Send us your CV and tell us what you want to build
          — if there is a fit, we will get back to you.
        </p>
        <a className="primary-action wide centered" href={careersMail}>
          <Mail size={16} strokeWidth={2} />
          Send Your CV
        </a>
      </section>

      <ContactSection />

      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <a className="footer-logo" href="#home" aria-label="Durgastra Innovations home">
              <Logo tone="light" />
            </a>
            <p>
              Advanced technologies and mission-ready solutions that dominate the spectrum, protect
              operations and ensure decision superiority.
            </p>
          </div>

          <div className="footer-column">
            <h4>Company</h4>
            <a href="#about">About Us</a>
            <a href="#solutions">Solutions</a>
            <a href="#products">Products</a>
            <a href="#careers">Careers</a>
          </div>

          <div className="footer-column">
            <h4>What We Do</h4>
            <a href="#products">UAVs &amp; Drones</a>
            <a href="#products">Software &amp; Simulation</a>
            <a href="#products">Maintenance &amp; Repair</a>
            <a href="#products">Drone Training</a>
          </div>

          <address className="footer-column footer-contact">
            <h4>Reach Us</h4>
            <span className="footer-address">
              <MapPin size={15} strokeWidth={1.7} />
              {CONTACT.address}
            </span>
            {CONTACT.phones.map(({ label, raw }) => (
              <a key={raw} href={`tel:+${raw}`}>
                <MessageCircle size={15} strokeWidth={1.7} />
                {label}
              </a>
            ))}
            <a href={`mailto:${CONTACT.email}`}>
              <Mail size={15} strokeWidth={1.7} />
              {CONTACT.email}
            </a>
          </address>
        </div>

        <div className="footer-base">
          <span>© {new Date().getFullYear()} Durgastra Innovations Pvt Ltd. All rights reserved.</span>
          <span className="footer-made">Designed &amp; built in India</span>
        </div>
      </footer>
    </main>
  );
};
